import * as Dialog from '@radix-ui/react-dialog';
import { X, Check } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { faengtAussenklickAb } from '@/onboarding/tourSchutz';
import { NUTZER_SYMBOLE } from '@/lib/nutzerSymbol';
import { C } from '@/theme';

/**
 * Das eigene Zeichen wählen (A9.1).
 *
 * WARUM ES DIESEN DIALOG BRAUCHT
 *   Das Symbol wird bei der ersten Anmeldung automatisch vergeben, wie
 *   die Farbe. Das genügt, damit niemand ohne dasteht — aber nicht für
 *   den Zweck. Ein Fremdheitssignal wirkt nur, wenn das eigene Zeichen
 *   VERTRAUT ist, und „ich bin die Tasse" sagt niemand über etwas, das
 *   ihm zugelost wurde. Erst die Wahl macht aus dem Merkmal eine
 *   Identität.
 *
 * JEDER NUR SICH SELBST
 *   Der Endpunkt nimmt keine fremde Nutzer-ID entgegen; er ändert immer
 *   die aus der Sitzung. Ein Zeichen, das ein anderer umstellen kann,
 *   wäre kein verlässliches Erkennungszeichen mehr.
 *
 * DIE EBENE IST 50, NICHT 75
 *   Beim ersten Anlauf hatte ich sie von „Nutzer hinzufügen" mitkopiert
 *   — und damit einen Dialog über das Scan-Fenster gelegt, der nichts
 *   mit dem Anmeldeweg zu tun hat. `scanEbene.test.tsx` hat es gefangen.
 *   Das Zeichen zu wählen ist ein gewöhnlicher Dialog: 50, wie Erfassung
 *   und Sendung (web/src/lib/ebenen.ts).
 *
 * DIE VORSCHAU TRÄGT DIE EIGENE FARBE
 *   Nicht Grau, nicht Teal: Der Chip soll hier so aussehen, wie er
 *   gleich in der Oberfläche aussieht. Wer zwischen zwei Motiven
 *   schwankt, entscheidet an ihrem Zusammenspiel mit der Farbe — und das
 *   sieht man nur, wenn beides zusammen dasteht.
 */
export const SYMBOL_WAEHLEN = 'Mein Zeichen wählen';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Die eigene Farbe — der Grund der Vorschau. */
  farbe: string | null;
  /** Das derzeit gewählte Symbol, oder null (dann: Initialen). */
  symbol: string | null;
  /** Die Initialen, für die Vorschau des Rückfalls. */
  kuerzel: string;
};

export function SymbolWahl({ open, onClose, farbe, symbol, kuerzel }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const flaeche = farbe ?? C.inkFaint;

  const setzen = useMutation({
    mutationFn: (neu: string) =>
      apiFetch<{ symbol: string | null }>('/nutzer/symbol.php', {
        method: 'POST',
        body: JSON.stringify({ symbol: neu })
      }),
    onSuccess: (antwort) => {
      // Der Chip steckt in der me-Antwort; ohne diese Zeile bliebe das
      // alte Zeichen stehen, bis jemand die Seite neu lädt.
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      void qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
      toast(
        'success',
        antwort.symbol === null ? 'Zeichen entfernt.' : 'Zeichen gespeichert.'
      );
      onClose();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : 'unbekannt';
      toast('error', `Zeichen konnte nicht gespeichert werden: ${code}`);
    }
  });

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/30" />
        <Dialog.Content
          onPointerDownOutside={faengtAussenklickAb}
          onInteractOutside={faengtAussenklickAb}
          aria-describedby={undefined}
          data-ueber-tour=""
          className="dialog-mobil-vollbild fixed left-1/2 top-1/2 z-50 w-[min(30rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-cream p-5 shadow-lg"
        >
          <div className="flex items-start justify-between gap-4">
            <Dialog.Title className="font-serif text-lg text-ink">
              {SYMBOL_WAEHLEN}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Schließen"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded text-ink-soft hover:bg-cream-dark"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <p className="mt-2 text-sm text-ink-soft">
            Dein Zeichen steht an allem, was Du erfasst. Es hilft, eine fremde
            Eingabe zu bemerken, bevor sie feststeht.
          </p>

          <div className="mt-4 grid grid-cols-4 gap-2">
            {NUTZER_SYMBOLE.map((s) => {
              const aktiv = s.code === symbol;
              return (
                <button
                  key={s.code}
                  type="button"
                  aria-pressed={aktiv}
                  disabled={setzen.isPending}
                  onClick={() => setzen.mutate(s.code)}
                  className={
                    'flex min-h-[76px] flex-col items-center justify-center gap-1.5 rounded-md border px-1 py-2 transition-colors ' +
                    (aktiv
                      ? 'border-akzent bg-akzent-sanft'
                      : 'border-line bg-cream hover:border-akzent')
                  }
                >
                  <span
                    aria-hidden
                    className="relative inline-flex h-11 w-11 items-center justify-center rounded-full"
                    style={{ backgroundColor: flaeche, color: C.cream }}
                  >
                    <s.Icon className="h-6 w-6" />
                    {aktiv && (
                      <span className="absolute -bottom-0.5 -right-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-cream text-akzent-stark">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] leading-none text-ink-soft">{s.name}</span>
                </button>
              );
            })}
          </div>

          {/* Der Rückfall ist ein ausdrücklicher Weg, kein Versehen: Wem
              keins der acht Motive zusagt, soll nicht eins tragen müssen,
              nur weil es einmal vergeben wurde. */}
          <button
            type="button"
            aria-pressed={symbol === null}
            disabled={setzen.isPending}
            onClick={() => setzen.mutate('')}
            className={
              'mt-3 flex min-h-[44px] w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left text-sm transition-colors ' +
              (symbol === null
                ? 'border-akzent bg-akzent-sanft text-akzent-stark'
                : 'border-line bg-cream text-ink-soft hover:border-akzent')
            }
          >
            <span
              aria-hidden
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-medium leading-none"
              style={{ backgroundColor: flaeche, color: C.cream }}
            >
              {kuerzel}
            </span>
            Kein Zeichen — meine Initialen zeigen
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
